import React, { useEffect, useState, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  Award, Save, Upload, Image, Palette, Eye, CheckCircle2,
  Signature, QrCode, Building2, AlignLeft, AlignCenter, AlignRight, MoveVertical,
} from 'lucide-react';
import { PageHeader, AppButton, AppCard, AppCardHeader, AppCardTitle, AppCardContent } from '../ui';
import CommonToaster from '../Common/CommonToaster';
import {
  getCertificateSettings,
  updateCertificateSettings,
  getUploadBackgroundUrl,
} from '../../utils/selfPacedApi';
import './_self-paced-pages.scss';

/* ── Built-in background presets (CSS gradients) ──────────────── */
const BACKGROUNDS = [
  { id: 'none', label: 'No Background', preview: '#ffffff' },
  { id: 'classic-gold', label: 'Classic Gold', preview: 'linear-gradient(135deg, #fdf6e3 0%, #f5e6c8 50%, #efe0b9 100%)' },
  { id: 'royal-blue', label: 'Royal Blue', preview: 'linear-gradient(135deg, #e8f0fe 0%, #d0dffd 50%, #c2d4fb 100%)' },
  { id: 'emerald', label: 'Emerald', preview: 'linear-gradient(135deg, #e6f7ef 0%, #c3eddb 50%, #a8e6cf 100%)' },
  { id: 'midnight', label: 'Midnight', preview: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' },
  { id: 'rose', label: 'Rose Gold', preview: 'linear-gradient(135deg, #fdf2f0 0%, #f8d7da 50%, #f0c0c8 100%)' },
  { id: 'parchment', label: 'Parchment', preview: 'linear-gradient(135deg, #fefcf3 0%, #f5ecd7 50%, #e8d9b5 100%)' },
  { id: 'custom', label: 'Custom Image', preview: null },
];

const TEMPLATES = [
  { id: 'classic', label: 'Classic', desc: 'Traditional gold border with serif typography' },
  { id: 'modern', label: 'Modern', desc: 'Clean lines with bold sans-serif typography' },
  { id: 'elegant', label: 'Elegant', desc: 'Ornamental corners with script accents' },
  { id: 'minimal', label: 'Minimal', desc: 'Simple layout with subtle accents' },
];

/* ── Sample data for preview ─────────────────────────────────── */
const SAMPLE = {
  studentName: 'Sarah Johnson',
  courseName: 'Advanced React & Node.js Mastery',
  certificateNo: 'CERT-2026-00042',
  issueDate: `Month of ${new Date().toLocaleDateString('en-US', { month: 'long' })} ${new Date().getFullYear()}`,
  completionPct: 100,
  quizScore: 92,
};

export default function CertificateSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [settings, setSettings] = useState({
    template: 'classic',
    primaryColor: '#174ea6',
    accentColor: '#c7942a',
    backgroundKey: '',
    backgroundUrl: '',
    signatoryName: '',
    signatoryTitle: '',
    showLogo: true,
    showQrCode: true,
    verticalOffset: 0,
    textAlign: 'center',
    instituteName: '',
    logoUrl: '',
  });
  const [selectedBg, setSelectedBg] = useState('classic-gold');
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data } = await getCertificateSettings();
      const d = data.data || data;
      setSettings(prev => ({ ...prev, ...d }));
      // Determine selected background
      if (d.backgroundKey) {
        setSelectedBg('custom');
      } else {
        setSelectedBg('classic-gold');
      }
    } catch {
      // Use defaults
    }
    setLoading(false);
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCertificateSettings({
        template: settings.template,
        primaryColor: settings.primaryColor,
        accentColor: settings.accentColor,
        backgroundKey: selectedBg === 'custom' ? settings.backgroundKey : '',
        signatoryName: settings.signatoryName,
        signatoryTitle: settings.signatoryTitle,
        showLogo: settings.showLogo,
        showQrCode: settings.showQrCode,
        verticalOffset: settings.verticalOffset,
        textAlign: settings.textAlign,
      });
      setToast({ show: true, message: 'Certificate settings saved', type: 'success' });
    } catch {
      setToast({ show: true, message: 'Failed to save settings', type: 'error' });
    }
    setSaving(false);
  };

  const handleUploadBackground = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setToast({ show: true, message: 'Please select an image file', type: 'error' });
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await getUploadBackgroundUrl(formData);
      const { s3Key } = data.data || data;

      handleChange('backgroundKey', s3Key);
      handleChange('backgroundUrl', URL.createObjectURL(file));
      setSelectedBg('custom');
      setToast({ show: true, message: 'Background uploaded', type: 'success' });
    } catch {
      setToast({ show: true, message: 'Upload failed', type: 'error' });
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getBgStyle = () => {
    if (selectedBg === 'custom' && settings.backgroundUrl) {
      return { backgroundImage: `url(${settings.backgroundUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    }
    if (selectedBg === 'none') return { background: '#ffffff' };
    const bg = BACKGROUNDS.find(b => b.id === selectedBg);
    return bg?.preview ? { background: bg.preview } : { background: '#ffffff' };
  };

  const isDarkBg = selectedBg === 'midnight';

  if (loading) {
    return <div className="d-flex justify-content-center my-5"><Spinner animation="border" /></div>;
  }

  return (
    <div className="cst-page">
      <PageHeader
        icon={Award}
        title="Certificate Design"
        description="Customize the certificate template issued to students on course completion"
      />

      <div className="cst-layout">
        {/* ── Left: Settings Panel ── */}
        <div className="cst-settings">
          {/* Template Selection */}
          <AppCard>
            <AppCardHeader><AppCardTitle><Palette size={16} /> Template Style</AppCardTitle></AppCardHeader>
            <AppCardContent>
              <div className="cst-template-grid">
                {TEMPLATES.map(t => (
                  <button
                    key={t.id}
                    className={`cst-template-option${settings.template === t.id ? ' cst-template-option--active' : ''}`}
                    onClick={() => handleChange('template', t.id)}
                  >
                    <span className="cst-template-option__name">{t.label}</span>
                    <span className="cst-template-option__desc">{t.desc}</span>
                    {settings.template === t.id && <CheckCircle2 size={16} className="cst-template-option__check" />}
                  </button>
                ))}
              </div>
            </AppCardContent>
          </AppCard>

          {/* Colors */}
          <AppCard>
            <AppCardHeader><AppCardTitle><Palette size={16} /> Colors</AppCardTitle></AppCardHeader>
            <AppCardContent>
              <div className="cst-color-row">
                <label className="cst-color-field">
                  <span>Primary Color</span>
                  <div className="cst-color-input">
                    <input
                      type="color"
                      value={settings.primaryColor}
                      onChange={(e) => handleChange('primaryColor', e.target.value)}
                    />
                    <input
                      type="text"
                      value={settings.primaryColor}
                      onChange={(e) => handleChange('primaryColor', e.target.value)}
                      className="cst-color-hex"
                      maxLength={7}
                    />
                  </div>
                </label>
                <label className="cst-color-field">
                  <span>Accent Color</span>
                  <div className="cst-color-input">
                    <input
                      type="color"
                      value={settings.accentColor}
                      onChange={(e) => handleChange('accentColor', e.target.value)}
                    />
                    <input
                      type="text"
                      value={settings.accentColor}
                      onChange={(e) => handleChange('accentColor', e.target.value)}
                      className="cst-color-hex"
                      maxLength={7}
                    />
                  </div>
                </label>
              </div>
            </AppCardContent>
          </AppCard>

          {/* Background */}
          <AppCard>
            <AppCardHeader><AppCardTitle><Image size={16} /> Background</AppCardTitle></AppCardHeader>
            <AppCardContent>
              <div className="cst-bg-grid">
                {BACKGROUNDS.map(bg => (
                  <button
                    key={bg.id}
                    className={`cst-bg-option${selectedBg === bg.id ? ' cst-bg-option--active' : ''}`}
                    onClick={() => {
                      setSelectedBg(bg.id);
                      if (bg.id === 'custom') fileInputRef.current?.click();
                    }}
                    title={bg.label}
                  >
                    {bg.id === 'custom' ? (
                      <div className="cst-bg-option__custom">
                        {settings.backgroundUrl ? (
                          <img src={settings.backgroundUrl} alt="Custom" />
                        ) : (
                          <Upload size={16} />
                        )}
                      </div>
                    ) : (
                      <div className="cst-bg-option__preview" style={{ background: bg.preview }} />
                    )}
                    <span className="cst-bg-option__label">{bg.label}</span>
                    {selectedBg === bg.id && <CheckCircle2 size={12} className="cst-bg-option__check" />}
                  </button>
                ))}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleUploadBackground}
                style={{ display: 'none' }}
              />
              {uploading && <div className="cst-uploading"><Spinner size="sm" animation="border" /> Uploading...</div>}
            </AppCardContent>
          </AppCard>

          {/* Signatory & Options */}
          <AppCard>
            <AppCardHeader><AppCardTitle><Signature size={16} /> Signatory & Options</AppCardTitle></AppCardHeader>
            <AppCardContent>
              <div className="cst-field">
                <label>Signatory Name</label>
                <input
                  type="text"
                  value={settings.signatoryName}
                  onChange={(e) => handleChange('signatoryName', e.target.value)}
                  placeholder="e.g. Dr. John Smith"
                  className="cst-input"
                />
              </div>
              <div className="cst-field">
                <label>Signatory Title</label>
                <input
                  type="text"
                  value={settings.signatoryTitle}
                  onChange={(e) => handleChange('signatoryTitle', e.target.value)}
                  placeholder="e.g. Director of Education"
                  className="cst-input"
                />
              </div>
              <div className="cst-toggles">
                <label className="cst-toggle">
                  <input
                    type="checkbox"
                    checked={settings.showLogo}
                    onChange={(e) => handleChange('showLogo', e.target.checked)}
                  />
                  <Building2 size={14} />
                  <span>Show Institute Logo</span>
                </label>
                <label className="cst-toggle">
                  <input
                    type="checkbox"
                    checked={settings.showQrCode}
                    onChange={(e) => handleChange('showQrCode', e.target.checked)}
                  />
                  <QrCode size={14} />
                  <span>Show QR Code</span>
                </label>
              </div>
            </AppCardContent>
          </AppCard>

          {/* Text Position */}
          <AppCard>
            <AppCardHeader><AppCardTitle><MoveVertical size={16} /> Text Position</AppCardTitle></AppCardHeader>
            <AppCardContent>
              <div className="cst-field">
                <label>Vertical Offset ({settings.verticalOffset > 0 ? '+' : ''}{settings.verticalOffset}%)</label>
                <div className="cst-slider-row">
                  <span className="cst-slider-label">Up</span>
                  <input
                    type="range"
                    min={-50}
                    max={50}
                    value={settings.verticalOffset}
                    onChange={(e) => handleChange('verticalOffset', Number(e.target.value))}
                    className="cst-slider"
                  />
                  <span className="cst-slider-label">Down</span>
                </div>
              </div>
              <div className="cst-field">
                <label>Text Alignment</label>
                <div className="cst-align-btns">
                  {[
                    { id: 'left', icon: AlignLeft, label: 'Left' },
                    { id: 'center', icon: AlignCenter, label: 'Center' },
                    { id: 'right', icon: AlignRight, label: 'Right' },
                  ].map(a => (
                    <button
                      key={a.id}
                      className={`cst-align-btn${settings.textAlign === a.id ? ' cst-align-btn--active' : ''}`}
                      onClick={() => handleChange('textAlign', a.id)}
                      title={a.label}
                    >
                      <a.icon size={16} />
                      <span>{a.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </AppCardContent>
          </AppCard>

          <AppButton onClick={handleSave} disabled={saving} className="cst-save-btn">
            {saving ? <Spinner size="sm" animation="border" /> : <Save size={16} />}
            {saving ? 'Saving...' : 'Save Settings'}
          </AppButton>
        </div>

        {/* ── Right: Live Preview ── */}
        <div className="cst-preview-wrap">
          <div className="cst-preview-label">
            <Eye size={14} /> Live Preview
          </div>
          <div className="cst-preview" style={getBgStyle()}>
            <div className="cst-cert">
              <div
                className="cst-cert__inner"
                style={{
                  transform: `translateY(${settings.verticalOffset}%)`,
                  textAlign: settings.textAlign,
                  alignItems: settings.textAlign === 'left' ? 'flex-start' : settings.textAlign === 'right' ? 'flex-end' : 'center',
                }}
              >
                {/* Institute logo + name */}
                <div className="cst-cert__institute" style={{ color: isDarkBg ? '#e2e8f0' : settings.primaryColor }}>
                  {settings.showLogo && settings.logoUrl ? (
                    <img src={settings.logoUrl} alt="" className="cst-cert__logo" />
                  ) : settings.showLogo ? (
                    <div className="cst-cert__logo-placeholder">
                      <Building2 size={28} />
                    </div>
                  ) : null}
                  <span>{settings.instituteName || 'Institute Name'}</span>
                </div>

                {/* Title */}
                <h2 className="cst-cert__title" style={{ color: isDarkBg ? '#f1f5f9' : settings.primaryColor }}>
                  Certificate of Completion
                </h2>

                {/* Presented-to line */}
                <p className="cst-cert__subtitle" style={{ color: isDarkBg ? '#94a3b8' : '#555' }}>
                  This Certificate is Proudly Presented To
                </p>

                {/* Student name with underline */}
                <div className="cst-cert__name-wrap">
                  <h3 className="cst-cert__name" style={{ color: isDarkBg ? '#f1f5f9' : '#1a1a2e' }}>
                    {SAMPLE.studentName}
                  </h3>
                  <div className="cst-cert__name-line" style={{ borderColor: isDarkBg ? '#475569' : '#333' }} />
                </div>

                {/* Course description */}
                <p className="cst-cert__subtitle" style={{ color: isDarkBg ? '#94a3b8' : '#555' }}>
                  For Successfully Completing the Course
                </p>
                <h4 className="cst-cert__course" style={{ color: isDarkBg ? '#e2e8f0' : settings.primaryColor }}>
                  "{SAMPLE.courseName}"
                </h4>

                {/* Date */}
                <p className="cst-cert__date" style={{ color: isDarkBg ? '#cbd5e1' : '#333' }}>
                  {SAMPLE.issueDate}
                </p>

                {/* Signatory */}
                {(settings.signatoryName || settings.signatoryTitle) && (
                  <div className="cst-cert__signatory">
                    {settings.signatoryName && (
                      <span className="cst-cert__sig-name" style={{ color: isDarkBg ? '#e2e8f0' : '#1a1a2e' }}>
                        {settings.signatoryName}
                      </span>
                    )}
                    {settings.signatoryTitle && (
                      <span className="cst-cert__sig-title" style={{ color: isDarkBg ? '#94a3b8' : '#777' }}>
                        {settings.signatoryTitle}
                      </span>
                    )}
                  </div>
                )}

                {/* Footer: cert no + QR */}
                <div className="cst-cert__footer">
                  <span style={{ color: isDarkBg ? '#64748b' : '#999' }}>{SAMPLE.certificateNo}</span>
                  {settings.showQrCode && (
                    <div className="cst-cert__qr-placeholder" style={{ borderColor: isDarkBg ? '#475569' : '#ddd' }}>
                      <QrCode size={20} style={{ color: isDarkBg ? '#475569' : '#bbb' }} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
      />
    </div>
  );
}
